import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

import { V1ClaimTokenResponse, V1GetTenantByHostnameResponse } from '../interface';
import { Result, ok, err } from '../utils/result';

@Injectable({
  providedIn: 'root',
})
export class IdbKeyvalService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly DB_NAME = 'keyval-store';
  private readonly STORE_NAME = 'keyval';
  private readonly DB_VERSION = 1;
  private readonly DEFAULT_TIMEOUT_MS = 5000;

  private async withTimeout<T>(
    promise: Promise<Result<T, Error>>,
    errorMsg = 'Operation timed out',
  ): Promise<Result<T, Error>> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<Result<T, Error>>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(err(new Error(errorMsg)));
      }, this.DEFAULT_TIMEOUT_MS);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'ok' in e) {
        return e as Result<T, Error>;
      }
      return err(e instanceof Error ? e : new Error(String(e)));
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  private dbPromise: Promise<Result<IDBDatabase, Error>> | null = null;

  // State management using Signals (Zoneless compatible)
  private readonly _currentDataTheme = signal<V1GetTenantByHostnameResponse | null>(null);
  private readonly _currentDataDevice = signal<V1ClaimTokenResponse | null>(null);
  private readonly _currentToken = signal<string | null>(null);
  private readonly _claimReceivedAt = signal<number | null>(null);

  // Expose as readonly signals to prevent external modification
  public readonly currentDataTheme = this._currentDataTheme.asReadonly();
  public readonly currentDataDevice = this._currentDataDevice.asReadonly();
  public readonly currentToken = this._currentToken.asReadonly();
  public readonly claimReceivedAt = this._claimReceivedAt.asReadonly();

  /**
   * Initializes or retrieves the IndexedDB connection.
   */
  private getDB(): Promise<Result<IDBDatabase, Error>> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    const p = new Promise<Result<IDBDatabase, Error>>((resolve) => {
      try {
        if (typeof indexedDB === 'undefined') {
          return resolve(err(new Error('IndexedDB is not supported')));
        }

        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

        request.onblocked = () => {
          console.warn('IndexedDB open blocked by another tab');
        };

        request.onerror = (event) => {
          const error = (event.target as IDBOpenDBRequest).error;
          resolve(err(error || new Error('Failed to open IndexedDB')));
        };

        request.onsuccess = (event) => {
          resolve(ok((event.target as IDBOpenDBRequest).result));
        };

        request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(this.STORE_NAME)) {
            db.createObjectStore(this.STORE_NAME);
          }
        };
      } catch (e: unknown) {
        const error = e instanceof Error ? e : new Error(String(e));
        resolve(err(error));
      }
    });

    this.dbPromise = this.withTimeout(p, 'Database open timed out').then((res) => {
      if (!res.ok) this.dbPromise = null;
      return res;
    });

    return this.dbPromise;
  }

  /**
   * Closes the IndexedDB connection.
   */
  public async close(): Promise<void> {
    if (this.dbPromise) {
      const dbResult = await this.dbPromise;
      if (dbResult.ok) {
        dbResult.value.close();
      }
      this.dbPromise = null;
    }
  }

  /**
   * Wrapper for IndexedDB 'get' operation.
   */
  private async get<T>(key: string): Promise<Result<T | undefined, Error>> {
    const dbResult = await this.getDB();
    if (!dbResult.ok) return dbResult;
    const db = dbResult.value;

    const p = new Promise<Result<T | undefined, Error>>((resolve) => {
      try {
        const transaction = db.transaction(this.STORE_NAME, 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.get(key);

        request.onerror = (event) => {
          const error = (event.target as IDBRequest).error;
          resolve(err(error || new Error('Get failed')));
        };
        request.onsuccess = (event) => resolve(ok((event.target as IDBRequest).result));
      } catch (e: unknown) {
        const error = e instanceof Error ? e : new Error(String(e));
        resolve(err(error));
      }
    });

    return this.withTimeout(p, `Get operation for key '${key}' timed out`);
  }

  /**
   * Wrapper for IndexedDB 'put' operation.
   */
  private async set(key: string, value: unknown): Promise<Result<void, Error>> {
    const dbResult = await this.getDB();
    if (!dbResult.ok) return dbResult;
    const db = dbResult.value;

    const p = new Promise<Result<void, Error>>((resolve) => {
      try {
        const transaction = db.transaction(this.STORE_NAME, 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);

        transaction.oncomplete = () => resolve(ok(undefined));
        transaction.onerror = (event) => {
          const error = (event.target as IDBTransaction).error;
          resolve(err(error || new Error('Transaction failed')));
        };
        transaction.onabort = () =>
          resolve(err(transaction.error || new Error('Transaction aborted')));

        store.put(value, key);
      } catch (e: unknown) {
        const error = e instanceof Error ? e : new Error(String(e));
        resolve(err(error));
      }
    });

    return this.withTimeout(p, `Set operation for key '${key}' timed out`);
  }

  /**
   * Updates device data in IndexedDB and signal state.
   */
  async updateDataDevice(data: V1ClaimTokenResponse): Promise<void> {
    if (!this.isBrowser) return;

    const result = await this.set('dataDevice', data);
    if (result.ok) {
      this._currentDataDevice.set(data);
    } else {
      this.handleError('updateDataDevice', result.error);
    }
  }

  /**
   * Fetches device data from IndexedDB and updates signal state.
   */
  async getDataDevice(): Promise<V1ClaimTokenResponse | null> {
    if (!this.isBrowser) return null;

    const result = await this.get<V1ClaimTokenResponse>('dataDevice');
    if (result.ok) {
      const device = result.value ?? null;
      this._currentDataDevice.set(device);
      return device;
    } else {
      this.handleError('getDataDevice', result.error);
      this._currentDataDevice.set(null);
      return null;
    }
  }

  /**
   * Updates theme data in IndexedDB and signal state.
   */
  async updateDataTheme(data: V1GetTenantByHostnameResponse): Promise<void> {
    if (!this.isBrowser) return;

    const result = await this.set('dataTheme', data);
    if (result.ok) {
      this._currentDataTheme.set(data);
    } else {
      this.handleError('updateDataTheme', result.error);
    }
  }

  /**
   * Fetches theme data from IndexedDB and updates signal state.
   */
  async getDataTheme(): Promise<V1GetTenantByHostnameResponse | null> {
    if (!this.isBrowser) return null;

    const result = await this.get<V1GetTenantByHostnameResponse>('dataTheme');
    if (result.ok) {
      const theme = result.value ?? null;
      this._currentDataTheme.set(theme);
      return theme;
    } else {
      this.handleError('getDataTheme', result.error);
      this._currentDataTheme.set(null);
      return null;
    }
  }

  /**
   * Stores the client-side timestamp when the claim response was received.
   */
  async updateClaimReceivedAt(ms: number): Promise<void> {
    if (!this.isBrowser) return;

    const result = await this.set('claimReceivedAt', ms);
    if (result.ok) {
      this._claimReceivedAt.set(ms);
    } else {
      this.handleError('updateClaimReceivedAt', result.error);
    }
  }

  /**
   * Fetches the stored claim-received timestamp from IndexedDB.
   */
  async getClaimReceivedAt(): Promise<number | null> {
    if (!this.isBrowser) return null;

    const result = await this.get<number>('claimReceivedAt');
    if (result.ok) {
      const ms = result.value ?? null;
      this._claimReceivedAt.set(ms);
      return ms;
    } else {
      this.handleError('getClaimReceivedAt', result.error);
      this._claimReceivedAt.set(null);
      return null;
    }
  }

  /**
   * Updates token data in IndexedDB and signal state.
   */
  async updateDataToken(token: string): Promise<void> {
    if (!this.isBrowser) return;

    const result = await this.set('dataToken', token);
    if (result.ok) {
      this._currentToken.set(token);
    } else {
      this.handleError('updateDataToken', result.error);
    }
  }

  /**
   * Fetches token data from IndexedDB and updates signal state.
   */
  async getDataToken(): Promise<string | null> {
    if (!this.isBrowser) return null;

    const result = await this.get<string>('dataToken');
    if (result.ok) {
      const token = result.value ?? null;
      this._currentToken.set(token);
      return token;
    } else {
      this.handleError('getDataToken', result.error);
      this._currentToken.set(null);
      return null;
    }
  }

  /**
   * Clears all data from the IndexedDB store and resets all signals.
   */
  async clearAll(): Promise<void> {
    if (!this.isBrowser) return;

    const dbResult = await this.getDB();
    if (!dbResult.ok) {
      this.handleError('clearAll', dbResult.error);
      return;
    }
    const db = dbResult.value;

    const p = new Promise<Result<void, Error>>((resolve) => {
      try {
        const transaction = db.transaction(this.STORE_NAME, 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {
          this._currentDataTheme.set(null);
          this._currentDataDevice.set(null);
          this._currentToken.set(null);
          this._claimReceivedAt.set(null);
          resolve(ok(undefined));
        };
        request.onerror = (event: Event) => {
          const error = (event.target as IDBRequest).error;
          resolve(err(error || new Error('Clear failed')));
        };
      } catch (e: unknown) {
        const error = e instanceof Error ? e : new Error(String(e));
        resolve(err(error));
      }
    });

    const result = await this.withTimeout(p, 'Clear operation timed out');
    if (!result.ok) {
      this.handleError('clearAll', result.error);
    }
  }

  private handleError(method: string, e: unknown): void {
    console.error(`IdbKeyvalService.${method} failed:`, e);
  }
}
